import { reactive } from "vue";
import { pick } from "lodash";
import useFetchChat from "../useFetch/useFetch";
import {
  chatHistory,
  chatQuestion,
  delApplyDialogApi,
  editChatQuestionApi,
} from "@/api/chatApi.js";
import { findModelById } from "./useModel";
import { CHAT_COMMAND } from "./constant";
import { sleep } from "@/utils/index";
import katex from "@/utils/katex.min.js";
import { judgeIsPreinstall, preinstallFetch } from "./preinstall";
import { testChatFetch } from "./testChat";
import { ElMessage } from "element-plus";
import { gptApi } from "@/config/apiDomain";
import { overdueToken } from "@/components/Dialogues/useData/login";
import { openCustomPopup } from "@/components/custom-popup/use/customPopup";
import { showVIPDialogue } from "@/components/Dialogues/useData/useDialogues";

const apiUrl = gptApi;
let chatType = null;

// 生成len位随机字符串有字母和数字组成
function randomString() {
  const len = 32;
  const strs = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz123456789";
  const maxPos = strs.length;
  let pwd = "";
  for (let i = 0; i < len; i++) {
    pwd += strs.charAt(Math.floor(Math.random() * maxPos));
  }
  return pwd;
}

const agentList = [
  "agent",
  "personify",
  "rewrite",
  "contentCompletion",
  "novelWrite",
  "contentWrite",
];

const getParams = (
  list,
  model,
  gptParams,
  dynamicOpt,
  modifyMsg,
  session_id,
  fileInfo
) => {
  // 查询is_clear_boundary是为ture的最后一个数据下标
  const clearIndex = list.findLastIndex((item) => item.is_clear_boundary);
  const arr = JSON.parse(JSON.stringify(list));
  arr.splice(0, clearIndex + 1);
  const messages = arr.slice(-100).reduce((acc, cur) => {
    const chat_file_local_path = cur?.fileInfo?.chat_file_local_path;
    const needPath =
      fileInfo?.chat_file_local_path === chat_file_local_path &&
      chat_file_local_path;
    return acc.concat([
      {
        role: cur.role,
        content: cur.is_deep_search
          ? cur.content.replace("深入搜索：", "")
          : cur.content,
        chat_file_local_path: needPath ? chat_file_local_path : undefined,
      },
    ]);
  }, []);
  modifyMsg && modifyMsg(messages);
  const data =
    model?.needParams?.reduce((acc, cur) => {
      if (typeof cur === "object") {
        return { ...acc, ...cur };
      }
      return {
        ...acc,
        [cur]: gptParams[cur],
      };
    }, {}) || {};
  data.messages = messages;
  if (["chat-normal"].includes(model?.id)) {
    data.auto_user_want = true;
  }
  // 4.0有文件上传再用意图判断
  if (["chat-plus"].includes(model?.id) && fileInfo) {
    data.auto_user_want = true;
    data.user_want = 8;
  }
  if (dynamicOpt?.user_want > 0) {
    data.auto_user_want = true;
  }
  // 动态增加的参数
  if (dynamicOpt) {
    const dynamicKeys = Object.keys(dynamicOpt);
    const value = {};
    dynamicKeys.forEach((key) => {
      if ([null, undefined].includes(dynamicOpt[key])) return;
      value[key] = dynamicOpt[key];
    });
    Object.assign(data, value);
  }
  if (agentList.includes(model?.id)) {
    data.system_key = session_id;
    data.contents = [messages[messages.length - 1].content];
  }
  data.session_id = session_id;
  return {
    url: apiUrl,
    data,
  };
};

// 获取聊天记录
const getChatHistoryList = async (listQuery) => {
  try {
    console.log("listQuery", listQuery);
    const res = await chatHistory(listQuery);
    console.log("res", res);

    const last_page = res.last_page || 1;
    const list = (res.list || [])
      .reduce((acc, cur) => {
        let userFileInfo = null;
        let userUrl = null;
        if (["multi-file-app", "multi_app"].includes(chatType)) {
          userUrl = cur.accessory_url;
        }
        if (cur.upload_accessory_url) {
          try {
            const uploadObj = JSON.parse(cur.upload_accessory_url);
            userFileInfo = {};
            Object.assign(userFileInfo, uploadObj);
            userUrl = uploadObj.url;
          } catch (error) {
            userUrl = cur.upload_accessory_url;
          }
        }
        // parse latex
        const isLatexSrt = isMdLateX(cur.answer);
        const isChartStr = isCharts(cur.answer);
        let answer_attachment = "";
        if (cur.answer_attachment) {
          answer_attachment = cur.answer_attachment;
        }
        return acc.concat([
          {
            role: "assistant",
            content: cur.answer,
            id: cur.message_id || cur.id,
            url: ["multi-file-app", "multi_app"].includes(chatType)
              ? undefined
              : cur.accessory_url,
            key: `${cur.id}-assistant`,
            isDone: true,
            refs: cur.web_search || cur.refs || [],
            related_issues: cur.related_issues,
            is_clear_boundary: cur.is_clear_boundary,
            is_deep_search: cur.is_deep_search,
            useVditorComponent: isLatexSrt || isChartStr,
            answer_attachment,
            links: cur?.links,
            created_at: cur?.created_at,
          },
          {
            role: "user",
            content: cur.question,
            id: cur.message_id || cur.id,
            url: userUrl,
            key: `${cur.id}-user`,
            fileInfo: userFileInfo,
            chat_file_content: cur.chat_file_content,
            created_at: cur?.created_at,
          },
        ]);
      }, [])
      .reverse();
    return {
      list,
      last_page,
    };
  } catch (e) {
    console.log("get chat history fail");
    console.log(e);
    throw new Error(e);
  }
};

const GPT_PARAMS = {
  model: 0,
  mode: 0,
  is_internet: false,
  is_version4: false,
  temperature: 1,
  is_deep_search: false,
};

/**
 * OPTION
 *  returnStreamText 是否返回流式文本 boolean
 *  extraHandlers 插件列表[]
 *    事件：{
 *
 *    }
 */
const useChat = (OPTION = {}) => {
  const extraHandlers = OPTION.extraHandlers || [];
  // 聊天列表数据
  const chatList = reactive([]);
  // isAwaiting: 是否正在等待回复
  // isReplying: 是否正在回复
  // isGetting: 是否正在获取聊天记录数据
  // model: 对话模型对象信息
  const chatStatus = reactive({
    isAwaiting: false,
    isReplying: false,
    isGetting: false,
    isGetingIssues: false,
    model: null,
  });
  // 绑定当前会话列表的一些对象
  const dialogueInfo = {
    dialogueStatus: null,
    dialogueList: null,
  };
  // 抛出状态的事件
  const exportEvent = {
    exportStatus: () => {
      //
    },
  };
  const defaultModel = findModelById("chat-normal");
  chatStatus.model = defaultModel;

  // 对话参数
  const gptParams = reactive(JSON.parse(JSON.stringify(GPT_PARAMS)));

  function resetGptParams() {
    const ori = JSON.parse(JSON.stringify(GPT_PARAMS));
    Object.assign(gptParams, ori);
  }

  const listQuery = {
    page: 1,
    pageSize: 10,
    session_id: "",
  };
  // 对话控制器

  let abortController = null;
  let keyAbortController = null;

  // 初始化数据
  let last_page = 1;
  // 当前 新建对话的
  let curIsNewDialogue = null;
  let chatId = "";
  const initChat = (sessionId, curDialogue, customModel = null) => {
    stopAbort();
    if (!sessionId) return;
    listQuery.session_id = sessionId;
    chatList.splice(0, chatList.length);
    chatStatus.isReplying = false;
    chatStatus.isAwaiting = false;
    last_page = 1;
    listQuery.page = 1;
    resetGptParams();
    chatType = curDialogue?.type;

    if (customModel) {
      // 处理自定义模型的情况
      chatStatus.model = customModel;
    } else if (chatType) {
      // 切换类型
      setType(
        ["default_app", "custom_app"].includes(chatType) ? "agent" : chatType
      );
    }
    // 判断当前是否是新对话，新对话不需要加载数据
    if (curDialogue && curDialogue.isNew) {
      curIsNewDialogue = curDialogue;
      return;
    }
    curIsNewDialogue = null;

    getChatList();
  };
  // 获取聊天记录
  const getChatList = async () => {
    chatStatus.isGetting = true;
    try {
      const { list, last_page: lastPage } = await getChatHistoryList(listQuery);
      last_page = lastPage;
      chatList.unshift(...list);
    } catch (e) {
      console.log("get chat history fail");
      console.log(e);
    } finally {
      chatStatus.isGetting = false;
    }
  };
  // 保存聊天记录
  const saveChat = async (params) => {
    const userObj = chatList[chatList.length - 2];
    const answerObj = chatList[chatList.length - 1];
    console.log("saveChat", userObj, answerObj);
    if (!userObj || !answerObj) return;
    let saveFileInfo;
    if (userObj.fileInfo) {
      saveFileInfo = pick(userObj.fileInfo, [
        "id",
        "name",
        "url",
        "ext",
        "size",
        "type",
        "chat_file_local_path",
        "originalName",
      ]);
    }

    const accessory_url = answerObj.url;
    // if (params.user_want == 10) {
    //   // 处理ppt情况
    //   const fileInfo = params.fileInfo;
    //   if (fileInfo) {
    //     accessory_url = JSON.stringify(fileInfo);
    //   }
    // }

    const recordParams = {
      session_id:
        dialogueInfo.dialogueStatus?.sessionId || listQuery.session_id,
      question: userObj.content,
      answer: answerObj.content,
      upload_accessory_url: JSON.stringify(saveFileInfo),
      chat_file_local_path: saveFileInfo
        ? saveFileInfo.chat_file_local_path
        : undefined,
      accessory_url,
      message_id: answerObj.id,
      refs: answerObj.refs,
      is_deep_search: answerObj.is_deep_search,
      answer_attachment: "",
      ...params,
    };
    const questionParams = {
      type: chatStatus.model?.id,
    };
    Object.assign(questionParams, recordParams);
    if (extraHandlers && extraHandlers.saveChat) {
      const newParams = extraHandlers.saveChat(questionParams);
      Object.assign(questionParams, newParams);
    }
    try {
      // console.log("save", questionParams);
      await chatQuestion(questionParams);
    } catch (err) {
      //
      console.log("saveerr = ", err);
    }
  };
  // 获取下一页数据
  const nextChatPage = async () => {
    if (listQuery.page + 1 > last_page) return;
    listQuery.page++;
    await getChatList();
  };
  // 删除一对消息
  const deleteChatObj = (id) => {
    // 获取所有匹配id的项
    const index = chatList.indexOf(chatList.find((item) => item.id === id));
    const length = chatList[index + 1].id === id ? 2 : 1;
    chatList.splice(index, length);
    const params = {
      message_id: id,
    };
    delApplyDialogApi(undefined, params);
  };
  // 删除消息
  const deleteChat = async (chatData) => {
    openCustomPopup({
      title: "确定要删除该消息吗？",
      content: "删除后此条消息将无法恢复和找回，请谨慎操作",
      confirmFun: async () => {
        const index = chatList.findIndex((item) => item.key === chatData.key);
        const id = chatList[index].id;
        try {
          const params = {
            message_id: id,
          };
          await delApplyDialogApi(undefined, params);
          chatList.splice(index, 1);
        } catch {
          chatList.splice(index, 1);
        }
      },
    });
  };
  // 清除消息记录
  const clearChat = async (opt = {}) => {
    const { success, fail } = opt;
    // openCustomPopup({
    //   title: "确定要清除该对话记录吗？",
    //   content: "清除后对话记录无法恢复和找回，请谨慎操作",
    //   confirmFun: async () => {
    //     if (chatStatus.isReplying) {
    //       stopAbort();
    //     }
    //     chatList.splice(0, chatList.length);
    //     delApplyDialogApi(undefined, {
    //       session_id: listQuery.session_id,
    //     }).finally(() => {
    //       ElMessage.success("清除记录成功");
    //       success && success();
    //     });
    //   },
    // });
  };
  // 设置对话模型
  const setType = (type) => {
    const newModel = findModelById(type);
    chatStatus.model = newModel;
    // !agentList.includes(type) &&
    // ElMessage({
    //   message: `已切换到${newModel.name}`,
    //   type: "success",
    //   duration: 800,s
    // });
  };
  // 发送消息
  const sendMsg = (msg, opts = {}) => {
    if (!msg && opts.forceEmptyMsg !== true)
      return ElMessage.error("请输入内容");
    const isPreinstall = judgeIsPreinstall(chatStatus.model.id, msg);
    chatId = randomString(16);
    // if (opts?.questionPrefix) {
    //   // 用户提问加上前缀提问内容
    //   msg = `${opts?.questionPrefix || ""} ${msg}`;
    // }
    if (opts?.fileInfo && opts?.fileInfo.isScan) {
      // 处理一下文件名

      const lastIndex = opts?.fileInfo.url.lastIndexOf("/");
      const lastFragment = opts?.fileInfo.url.substring(lastIndex + 1);
      opts.fileInfo.name = lastFragment;
    }

    const userObj = {
      role: "user",
      id: chatId,
      key: `${chatId}-user`,
      content: msg,
      url: opts?.url,
      fileInfo: opts?.fileInfo || null,
      is_deep_search: opts?.is_deep_search,
      chat_file_content: opts?.chat_file_content || null,
    };
    let _userObj = userObj;
    if (extraHandlers && extraHandlers.initUserObjMsg) {
      _userObj = extraHandlers.initUserObjMsg(_userObj, opts);
    }
    chatList.push(_userObj);
    try {
      const curSessionId = dialogueInfo.dialogueStatus?.sessionId;
      const curDialogue = dialogueInfo.dialogueList?.find(
        (item) => item.session_id === curSessionId
      );
      // 判断当前是否是新对话
      if (curDialogue && curDialogue.isNew) {
        curIsNewDialogue = curDialogue;
        curIsNewDialogue.isNew = false;
        curIsNewDialogue.title = userObj.content;
        curIsNewDialogue.type = chatStatus.model?.id;
        curIsNewDialogue = null;
      } else {
        curIsNewDialogue = null;
      }
    } catch (error) {
      console.log("error", error);
    }
    // 如果是画图的则不做总结
    if (
      chatStatus.model?.getRelatedIssues &&
      opts?.chatOption?.user_want !== 3 &&
      !isPreinstall
    ) {
      getKeyList(apiUrl, msg);
    }
    const getAnswerOpts = {
      isPreinstall,
      ...(opts || {}),
    };
    getAnswer(getAnswerOpts);
  };
  // 获取答案
  const getAnswer = async (opts) => {
    const params = getParams(
      chatList,
      chatStatus.model,
      gptParams,
      opts?.chatOption,
      (message) => {
        // console.log("getAnswer-opts", opts);
        const userMessage = message[message.length - 1];
        const user_want = opts?.chatOption?.user_want;
        if (opts.fileInfo) {
          // 如果是阅读总结
          if (user_want === 4 || user_want === 8) {
            userMessage.chat_file_path = opts.fileInfo?.url || undefined;
            userMessage.chat_file_name = opts.fileInfo?.name || undefined;
            // 引用类型使用 oriFile 里面的字段
            userMessage.chat_file_local_path =
              opts.fileInfo?.chat_file_local_path ||
              opts.fileInfo?.oriFile?.chat_file_local_path ||
              undefined;
            // 阅读总结后面不加 url
          } else {
            // 用户提问加上链接提问
            userMessage.content = `${userMessage.content} ${
              opts?.fileInfo?.url || ""
            }`;
          }
        }
        if (opts?.chat_file_content) {
          userMessage.chat_file_content = opts?.chat_file_content || null;
        }
      },
      dialogueInfo.dialogueStatus?.sessionId || listQuery.session_id,
      opts?.fileInfo
    );
    if (opts?.is_deep_search) {
      params.data.is_deep_search = opts?.is_deep_search;
    }
    let fetchChat;
    if (opts.isPreinstall) {
      fetchChat = preinstallFetch;
    } else {
      fetchChat = useFetchChat;
    }
    // if (process.env.NODE_ENV !== "production") {
    //   fetchChat = testChatFetch;
    // }
    const { abortController: abort, getFetchResult } = fetchChat(params);
    abortController = abort;
    chatStatus.isReplying = true;
    chatStatus.isAwaiting = true;
    let _answerObj = {
      answer_attachment: "",
      role: "assistant",
      id: chatId,
      key: `${chatId}-assistant`,
      content: "",
      // 该对话是否完成
      isDone: false,
      // 回答内容的一些状态
      status: {
        command: CHAT_COMMAND[opts?.chatOption?.user_want]?.command || "",
        statusText: CHAT_COMMAND[opts?.chatOption?.user_want]?.status_1 || "", // 当前状态的文本
        // 新的对话可以自动播放
        isCanAutoPlayVoice: true,
      },
      url: "",
      related_issues: [],
      refs: [],
      is_deep_search:
        opts?.is_deep_search || params.data?.is_deep_search || false,
      chat_file_percent: "",
      useVditorComponent: false, // 是否使用 vditor 组件
      deepseekR1: {},
    };
    if (extraHandlers && extraHandlers.initAnswerObjMsg) {
      _answerObj = extraHandlers.initAnswerObjMsg(_answerObj);
    }
    const answerObj = reactive(_answerObj);
    chatList.push(answerObj);
    let returnStreamText =
      OPTION.returnStreamText !== undefined ? OPTION.returnStreamText : true;
    if (["GPTo1"].includes(chatStatus.model?.id)) {
      returnStreamText = false;
    }
    const control = {
      returnStreamText,
    };
    await getFetchResult(
      {
        accept: async (msg) => {
          if (extraHandlers && extraHandlers.formatAcceptMsg) {
            msg = extraHandlers.formatAcceptMsg(msg, {
              answerObj,
            });
          }
          // console.log("msg", msg);
          if (typeof msg === "object") {
            // deepseek 的思考内容
            if (msg.reasoning_content) {
              let reasoning_content = msg.reasoning_content || "";
              if (!answerObj.content && !reasoning_content.startsWith("\n")) {
                answerObj.content = "> ";
              }
              reasoning_content = reasoning_content.replaceAll(
                /\n+/g,
                "\n>\n>"
              );
              chatStatus.isAwaiting = false;
              const subStrLength = reasoning_content.length;
              for (let i = 0; i < reasoning_content.length; i++) {
                if (subStrLength === 1) {
                  await sleep(28);
                } else if (subStrLength === 2) {
                  await sleep(20);
                } else {
                  await sleep(10);
                }
                answerObj.content += reasoning_content[i];
              }
              // '\n嗯，用户让我猜他叫什么名字，这有点挑战性呢。首先，我需要考虑用户为什么会提出这样的问题。可能是想测试我的能力，或者只是开个玩笑。由于我无法访问用户的具体信息，比如个人数据或账户详情，所以直接猜中的可能性几乎为零。\n\n接下来，我得想想如何回应才能既友好又不让用户失望。可能需要用幽默的方式来化解，或者提供其他帮助的可能性。比如，我可以建议用户告诉我他们的名字，或者转移话题到其他功能上，比如起名字的建议。\n\n另外，用户可能并不是真的想让我猜名字，而是有其他潜在的需求。比如他们可能想测试我的反应，或者希望有一个有趣的互动。这时候保持积极和友好的态度很重要，同时也要明确我的限制，不能访问个人信息。\n\n还需要考虑语言的表达方式，避免让用户觉得被敷衍。或许可以加入一些俏皮话，比如提到常见的名字或者用比喻的方式，让回答更有趣。同时，保持回应的简洁，不让用户等待太久。\n\n最后，确保回答符合规定的格式，不使用markdown，保持自然的口语化中文。检查有没有遗漏的信息，确保所有的点都覆盖到，比如我的限制、提供帮助的方向，以及保持'
              // '> 好的，用户让我猜他的名字。首先，我需要考虑用户可能的意图。他可能是在开玩笑，或者想测试我的能力。不过，作为AI，我并没有访问用户个人信息的权限，包括名字。这时候需要礼貌地回应，同时保持友好和幽默。\n\n>接下来，我要确保回应清晰，不让用户误解我能获取私人数据。可能需要加入一些轻松的元素，比如提到可以帮他起名字，或者用其他方式转移话题。同时，要符合规定的格式，不使用markdown，保持口语化。\n\n>还要检查有没有重复或冗余的内容，确保回答简洁。比如，避免多次强调无法获取信息，而是提供替代方案。例如，建议用户分享名字或询问是否需要帮忙起名，这样既回答了问题，又保持了互动性。\n\n>另外，注意用户可能的需求，也许他真的希望有一个昵称，或者想进行有趣的交流。这时候提供起名的选项能增加参与感。同时，保持语气友好，用表情符号增加亲切感，但不要过多，符合规定。\n\n>最后，确保整个回答符合中文表达习惯，自然流畅，没有语法错误。检查有没有违反任何规则的内容，比如不提供隐私信息，不越权操作。'
            }
            if (msg.clearMsg) {
              answerObj.content = `${
                opts?.chatOption?.user_want
                  ? `${opts?.chatOption?.user_want}-`
                  : ""
              }存在敏感词；`;
            }
            if (msg.chat_file_percent) {
              answerObj.chat_file_percent = msg.chat_file_percent;
            }
            if (msg.chat_file_local_path && opts.fileInfo) {
              opts.fileInfo.chat_file_local_path = msg.chat_file_local_path;
            }
            // 获取当前指令
            if (msg.user_want) {
              answerObj.status.command = CHAT_COMMAND[msg.user_want]?.command;
              answerObj.status.statusText =
                CHAT_COMMAND[msg.user_want]?.status_1 || "理解问题";
              // 画图的时候终止请求
              if (msg.user_want === 3) {
                if (keyAbortController) {
                  keyAbortController.abort();
                  keyAbortController = null;
                }
              }
            }
            // dalle3，返回图片的情况
            if (msg.response?.image_urls) {
              const firstImg = msg.response?.image_urls[0] || "";
              answerObj.url = firstImg;
              answerObj.content = msg.response.rephraser_result || "";
            }
            // 联网，返回数组
            if (msg.web_search) {
              answerObj.refs = msg.web_search || [];
            }
            if (msg.references && msg.references.length > 0) {
              const refs = msg.references
                .filter((item) => item.url && item.title && item.logo_url)
                .map((item) => ({
                  link: item.url,
                  title: item.title,
                  icon: item.logo_url,
                  media: item.site_name,
                }));
              answerObj.refs = refs;
            }
          } else {
            chatStatus.isAwaiting = false;
            answerObj.content += msg;
          }
        },
        done: async () => {
          chatStatus.isAwaiting = false;
          chatStatus.isReplying = false;
          answerObj.isDone = true;
          answerObj.status.isCanAutoPlayVoice = false;
          if (extraHandlers && extraHandlers.answerDone) {
            extraHandlers.answerDone({
              answerObj,
            });
          }
          const [formatText, isLateX] = formatWithLateX(answerObj.content);

          if (isLateX || isCharts(answerObj.content)) {
            answerObj.useVditorComponent = true;
          }
          if (formatText?.trim() !== "") {
            answerObj.content = formatText;
          }
          if (answerObj.content.trim() === "") {
            answerObj.content = `${
              opts?.chatOption?.user_want
                ? `${opts?.chatOption?.user_want}-`
                : ""
            }回复失败，请重新提问。`;
          }
          saveChat({
            user_want: opts?.chatOption?.user_want,
            question_pendant: opts?.questionPrefix,
            chat_file_content: opts?.chat_file_content || null,
          }).then(() => {
            if (answerObj.related_issues.length > 0) {
              editChatQuestion(answerObj.related_issues);
            }
          });
        },
        error: (code, errorObj) => {
          chatStatus.isReplying = false;
          chatStatus.isAwaiting = false;
          answerObj.isDone = true;
          answerObj.status.isCanAutoPlayVoice = false;
          if (extraHandlers && extraHandlers.chatErr) {
            extraHandlers.chatErr({
              code,
              errorObj,
            });
          }
          exportEvent?.exportStatus({ errCode: code });
          if (errorObj?.message === "user_abort") {
            const lastMsg = chatList[chatList.length - 1];
            if (
              lastMsg &&
              lastMsg.role === "assistant" &&
              lastMsg.content !== ""
            ) {
              // 用户手动取消，也保存聊天记录
              saveChat({
                user_want: opts?.chatOption?.user_want,
                question_pendant: opts?.questionPrefix,
                chat_file_content: opts?.chat_file_content || null,
              }).then(() => {
                if (answerObj.related_issues.length > 0) {
                  editChatQuestion(answerObj.related_issues);
                }
              });
            }
          }
          switch (code) {
            case 402:
              answerObj.content = "请先购买会员";
              showVIPDialogue.value = true;
              break;
            case 403:
              overdueToken();
              break;
            case 410: {
              const sensitive_words = errorObj.data?.sensitive_words;
              if (sensitive_words) {
                answerObj.content = `存在敏感词汇，无法作答。 敏感词：${sensitive_words}。`;
              } else {
                answerObj.content = "存在敏感词汇，无法作答";
              }
              answerObj.is_clear_boundary = true;
              saveChat({
                is_clear_boundary: true,
              });
              break;
            }

            case 423: {
              const sensitive_words = errorObj.data?.sensitive_words;

              if (sensitive_words) {
                answerObj.content = `存在敏感词汇，无法作答。 敏感词：${sensitive_words}。`;
              } else {
                answerObj.content = "存在敏感词汇，无法作答";
              }
              answerObj.is_clear_boundary = true;
              saveChat({
                is_clear_boundary: true,
              });
              // console.log("423");
              break;
            }
            case 500:
              if (answerObj.content === "")
                answerObj.content += "回答失败，当前模型请求过载，请稍后再试";
              break;
            case 416:
              answerObj.content =
                errorObj?.data?.message || "网络异常，请稍后再试";
              if (keyAbortController) {
                keyAbortController.abort();
              }
              break;
            default:
              answerObj.content += "网络异常，请稍后再试";
              console.log("default error");
          }
        },
        reset: () => {
          answerObj.content = "回答失败，当前服务器繁忙，请稍后重新提问。";
        },
      },
      control
    );
  };
  // 停止请求
  const stopAbort = () => {
    if (abortController) {
      const stopAbort = {
        code: 500,
        message: "user_abort",
      };
      abortController.abort(stopAbort);
      abortController = null;
      chatStatus.isAwaiting = false;
      chatStatus.isReplying = false;
      const lastMsg = chatList[chatList.length - 1];
      if (lastMsg && lastMsg.role === "assistant" && lastMsg.content === "") {
        chatList.pop();
      }
    }
    if (keyAbortController) {
      keyAbortController.abort();
      keyAbortController = null;
    }
  };

  function bindDialogue(key, value) {
    dialogueInfo[key] = value;
  }

  function bindExportEvents(key, value) {
    if (typeof value === "function") {
      exportEvent[key] = value;
    }
  }

  const getKeyList = async (url, val) => {
    const params = {
      url,
      data: {
        non_chat: true,
        system_key: "question.generate",
        response_format: { type: "json_object" },
        contents: [val],
      },
    };
    let content = "";
    const { abortController: abort, getFetchResult } = useFetchChat(params);
    keyAbortController = abort;
    chatStatus.isGetingIssues = true;
    await getFetchResult(
      {
        accept: (msg) => {
          if (typeof msg === "string") {
            content += msg;
          }
        },
        done: () => {
          chatStatus.isGetingIssues = false;
          const recommendContent = content
            .replace("```json", "")
            .replace("```", "")
            .trim();
          try {
            const obj = JSON.parse(recommendContent);
            const list = obj.questions || [];
            const answerObj = chatList[chatList.length - 1];
            if (!answerObj || answerObj.role === "user") return;
            answerObj.related_issues = list;
            if (answerObj.isDone) {
              editChatQuestion(list);
            }
          } catch {
            //
          }
        },
        error: () => {
          chatStatus.isGetingIssues = false;
        },
        reset: () => {
          //
        },
      },
      {
        returnStreamText: false,
      }
    );
  };
  const editChatQuestion = async (list) => {
    const params = {
      message_id: chatId,
      session_id: dialogueInfo.dialogueStatus?.sessionId,
      related_issues: list,
    };
    editChatQuestionApi(params);
  };

  // 清空上下文
  const clearContext = async (opt = {}) => {
    const { success, fail } = opt;
    // session_id: ,
    const params = {
      message_id: chatList[chatList.length - 1].id,
      session_id: dialogueInfo.dialogueStatus?.sessionId,
      is_clear_boundary: true,
    };
    try {
      await editChatQuestionApi(params);
      chatList[chatList.length - 1].is_clear_boundary = true;
      ElMessage.success("已清除上下文");
      success && success();
    } catch (e) {
      ElMessage.success(`清空上下文失败：${e.message}`);
      fail && fail(e);
    }
  };

  const editPPTInfo = async (id, sid, newInfo) => {
    const params = {
      message_id: id,
      session_id: sid,
      answer_attachment: newInfo,
    };

    try {
      await editChatQuestionApi(params);
    } catch (error) {
      console.log("editPPTInfo error = ", error);
    }
  };

  const addPPTInfoToChatList = async (params) => {
    const { user_want, fileInfo } = params;

    // 记录文件挂载在answerObject上
    if (user_want != 10) {
      console.log("错误的user_want");
      return;
    }

    if (!fileInfo) {
      console.log("错误的文件信息");

      return;
    }

    const id = fileInfo.id; // 最佳搜索项
    const session_id = fileInfo.sid;
    if (!id || !session_id) {
      console.log("聊天信息拿不到");
      return;
    }

    const answerObject = chatList.find((item) => {
      const _id = item.id ?? "";
      if (_id == id && item.role == `assistant`) {
        return true;
      }
      return false;
    });

    // 会重复写入
    const accessory_url = JSON.stringify(fileInfo);
    try {
      await editPPTInfo(id, session_id, accessory_url);
      if (answerObject) {
        answerObject.answer_attachment = accessory_url;
      }
    } catch (error) {
      console.log("addPPTInfoToChatList error=", error);
    }
  };

  return {
    chatList,
    chatStatus,
    gptParams,
    initChat,
    saveChat,
    nextChatPage,
    setType,
    stopAbort,
    sendMsg,
    deleteChat,
    deleteChatObj,
    clearChat,
    bindDialogue,
    clearContext,
    bindExportEvents,
    addPPTInfoToChatList,
  };
};

function isMdLateX(text = "") {
  const reg = /(\$)?\$(.*?)\$(\$)?/g;
  const result = [...text.matchAll(reg)];
  if (result && result.length) {
    return true;
  }
  return false;
}

function isMatrix(text) {
  const matrix = /matrix/g;
  const result = [...text.matchAll(matrix)];
  return result?.length > 0;
}

function isLateX(text) {
  try {
    katex.renderToString(text);
    return true;
  } catch (e) {
    console.log("e", e);
    return false;
  }
}

// 处理 LateX 公式
function formatWithLateX(text = "") {
  if (!text) return [text, false];

  // 先处理 Markdown 风格的 $...$ 或 $$...$$
  const isMdLateXFlag = isMdLateX(text);
  let newText = text;
  let isLateXFlag = false;

  // 匹配和处理 \\[ ... \\] 或 \\( ... \\) 的内容
  const reg = /\\[[|(](.*?)\\[\])]/gs;
  const matches = [...newText.matchAll(reg)];

  if (matches && matches.length) {
    matches.forEach((item) => {
      const content = item[1];
      if (!isMatrix(content)) {
        const strippedContent = content.replace(/\\/g, ""); // 去掉转义符号
        isLateXFlag = isLateX(strippedContent);
      } else {
        isLateXFlag = true;
      }

      if (isLateXFlag) {
        // 替换 \\[...\] 或 \\(...\) 为 $...$ 或 $$...$$
        newText = newText.replace(item[0], (match) =>
          match.includes("\\(")
            ? `$${content.trim()}$`
            : `$$${content.trim()}$$`
        );
      }
    });
  }
  const finalFlag = isMdLateXFlag || isLateXFlag;
  return [newText, finalFlag];
}

function isTable(text = "") {
  const lines = text.trim().split("\n"); // 是否有分隔符行
  const hasSeparatorLine = lines.some((line) =>
    /^(\|?\s*-+\s*)+\|?$/.test(line)
  );
  // 每行是否符合表格结构
  const isTableStructure = lines.every(
    (line) => line.includes("|") // 表头行、分隔符行和数据行都应该包含 `|`，并以 `|` 分隔列
  );
  return hasSeparatorLine && isTableStructure;
}

function isCharts(text = "") {
  const EchartsReg = /```echarts[\s\S]*?```/;
  const MermaidReg = /```mermaid[\s\S]*?```/;
  const MindmapReg = /```mindmap[\s\S]*?```/;
  return (
    EchartsReg.test(text) ||
    MermaidReg.test(text) ||
    MindmapReg.test(text) ||
    isTable(text)
  );
}

export default useChat;
